import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Legend,
  LineController,
  LinearScale,
  PieController,
  SubTitle,
  Title,
  Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { ComponentAbstract } from '@app/components/abstract/component.abstract';
import { ChartTypes } from '@dashboards/models/chart-types.enum';
import { EColorPalette } from '@shared/enums/color-palette.enum';
import { ChartConfig, ChartDataItem } from '@shared/interfaces/chart-data-item';
import { CustomCurrencyDotsCommasPipe } from '@shared/pipes/custom-currency-dots-commas.pipe';
import { ChartService } from '@ui-components/components/chart/services/chart.service';

Chart.register(
  ArcElement,
  BarElement,
  BarController,
  LineController,
  PieController,
  CategoryScale,
  LinearScale,
  Legend,
  Title,
  Tooltip,
  SubTitle,
  ChartDataLabels
);

Chart.defaults.set('plugins.datalabels', {
  display: false,
});

@UntilDestroy()
@Component({
  selector: 'app-chart',
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartComponent extends ComponentAbstract implements OnInit, OnChanges {
  id = `chart_${new Date().getTime()}`;
  chart: Chart;
  dataListBSubject: BehaviorSubject<ChartDataItem[]> = new BehaviorSubject<ChartDataItem[]>([]);
  canvasBSubject: BehaviorSubject<ElementRef> = new BehaviorSubject<ElementRef>(null);
  _chartDataList: ChartDataItem[] = [];
  itemCount = 0;
  dataSum = 0;
  activeLegend: { label: string; value: number } | null = null;

  previousCanvas = null;

  @Input() disableAnimation = false;
  @Input() width = 100;
  @Input() height = 100;
  @Input() chartType = ChartTypes.PIE;
  @Input() displayLegend = true;
  @Input() displayLegendBottom = false;
  @Input() displayLegendInside = false;
  @Input() displayTotalInside = false;
  @Input() emptyTemplate: TemplateRef<any>;
  @Input() emptyChartLabel = 'No data to display';
  @Input() chartConfig: ChartConfig = {
    showTooltip: true,
    showDataLabel: true,
    cutoutLevel: 70,
  };
  @Input() displayPercentageInLegend = false;
  @Input() useCustomTooltipFormatter = false;
  @Input() useCurrencyPipe = false;
  @Input() hoveredLabel: string | null = null;

  @Input() containerCss = '';
  @Input() legendCss = 'body-description';

  @Input() clickableLabel = false;
  @Input() labelActiveColor?: EColorPalette;
  @Input() labelBlurColor?: EColorPalette;

  @Input() chartMaxWidth?: string;

  @Input() set chartDataList(chartDataList: ChartDataItem[]) {
    this.itemCount = chartDataList?.length;
    this.dataListBSubject.next(chartDataList);

    this.cdr.detectChanges();
  }

  @Output() itemClick = new EventEmitter<ChartDataItem>();
  @Output() itemHover = new EventEmitter<ChartDataItem | null>();

  @ViewChild('canvas', { static: false }) set content(canvas: ElementRef) {
    this.canvasBSubject.next(canvas);

    this.cdr.detectChanges();
  }

  constructor(protected cdr: ChangeDetectorRef, private renderer: Renderer2, private chartService: ChartService) {
    super(cdr);
  }

  private externalHoverNodeKey: string | null = null;
  private externalHoverValue: number | null = null;

  ngOnInit(): void {
    combineLatest([this.dataListBSubject, this.canvasBSubject])
      .pipe(
        untilDestroyed(this),
        filter(([chartDataList, canvas]) => !!chartDataList?.length && !!canvas),
        tap(([chartDataList, canvas]) => {
          this._chartDataList = chartDataList;

          if (this.displayLegendInside && !this.activeLegend && chartDataList?.length > 0) {
            const largestValueItem = chartDataList.reduce((maxItem, currentItem) => {
              return currentItem.value > (maxItem?.value || 0) ? currentItem : maxItem;
            }, null);

            if (largestValueItem) {
              this.activeLegend = { label: largestValueItem.label, value: largestValueItem.value };
            }
          }

          this.chartService.getChartData(chartDataList);
          this.dataSum = this.chartService.getDataSum();
          this.chart = this.createChart(canvas);

          this.cdr.detectChanges();
        })
      )
      .subscribe()
      .untilDestroyed(this);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.useCustomTooltipFormatter && changes['hoveredLabel'] && this.chart && this._chartDataList?.length) {
      const hoveredLabel = this.hoveredLabel;
      if (!hoveredLabel) {
        this.chart.setActiveElements([]);
        this.chart.tooltip?.setActiveElements([], { x: 0, y: 0 });
        this.chart.update();
        this.externalHoverNodeKey = null;
        this.externalHoverValue = null;
        return;
      }

      const hoveredIndex = this._chartDataList.findIndex(i => i.nodeKey === hoveredLabel || i.label === hoveredLabel);

      if (hoveredIndex < 0) {
        this.chart.setActiveElements([]);
        this.chart.tooltip?.setActiveElements([], { x: 0, y: 0 });
        this.chart.update();
        this.externalHoverNodeKey = null;
        this.externalHoverValue = null;
        return;
      }

      const hoveredItem = this._chartDataList[hoveredIndex];
      const hoveredKey = hoveredItem.nodeKey ?? '';
      this.externalHoverNodeKey = hoveredKey;
      const hoveredValue =
        typeof hoveredItem.additionalValue === 'number'
          ? hoveredItem.additionalValue
          : typeof hoveredItem.value === 'number'
          ? hoveredItem.value
          : null;
      this.externalHoverValue = hoveredValue;

      const exactMatches: number[] = [];
      const descendantMatches: number[] = [];

      this._chartDataList.forEach((item, idx) => {
        const key = item.nodeKey ?? '';
        if (!key) return;
        if (key === hoveredKey) {
          exactMatches.push(idx);
        } else if (hoveredKey && key.startsWith(`${hoveredKey}|`)) {
          descendantMatches.push(idx);
        }
      });

      const relatedIndices =
        exactMatches.length > 0
          ? [...exactMatches, ...descendantMatches]
          : descendantMatches.length
          ? descendantMatches
          : [hoveredIndex];

      const activeIndices = relatedIndices.filter(index => {
        const value = this._chartDataList[index]?.value ?? 0;
        const key = this._chartDataList[index]?.nodeKey ?? '';
        if (key === hoveredKey) {
          return true;
        }
        return typeof value === 'number' && value > 0;
      });

      if (!activeIndices.length) {
        this.chart.setActiveElements([]);
        this.chart.tooltip?.setActiveElements([], { x: 0, y: 0 });
        this.chart.update();
        return;
      }

      requestAnimationFrame(() => {
        if (!this.chart) return;

        const activeElements = activeIndices.map(index => ({ datasetIndex: 0, index }));

        this.chart.setActiveElements(activeElements);
        this.chart.tooltip?.setActiveElements(activeElements, { x: 0, y: 0 });
        this.chart.update();
      });
    }
  }

  showActiveElement(index) {
    if (this.chart) {
      this.chart.setActiveElements([{ datasetIndex: 0, index }]);
      this.chart.update();
    }
  }

  reset() {
    if (this.chart) {
      this.chart.setActiveElements([]);
    }
  }

  changeActiveSelection(item: ChartDataItem): void {
    if (this.clickableLabel && item.value > 0) {
      if (this.displayLegendInside) this.activeLegend = { label: item.label, value: item.value };
      const formattedChartData = this._chartDataList.map(a => ({
        ...a,
        backgroundColor: !a.value
          ? EColorPalette.cGray8
          : item.value && a.label === item.label
          ? this.labelActiveColor
          : this.labelBlurColor,
      }));

      this.dataListBSubject.next(formattedChartData);
    }
  }

  private createChart(canvas: ElementRef): Chart {
    this.destroyChart();

    const baseConfig = this.chartService.getChartConfig(this.chartType, this._chartDataList, this.chartConfig);

    if (!this.chartConfig.clickable) {
      return new Chart(canvas.nativeElement, baseConfig);
    }

    const pluginsBlock = this.useCustomTooltipFormatter
      ? {
          datalabels: { display: () => false },
          tooltip: {
            displayColors: true,
            callbacks: {
              title: context => {
                const chartInstance = context?.chart;
                const activeElements = chartInstance?.getActiveElements?.() ?? [];

                if (activeElements.length > 1 && context[0]?.dataIndex === activeElements[0].index) {
                  const parentIndex = activeElements[0].index;
                  const parentLabel = chartInstance?.data?.labels?.[parentIndex] ?? 'Group';
                  return parentLabel.split('→')[0].trim();
                }

                let label = context[0]?.label ?? 'N/A';

                if (context.length > 1) {
                  label = label.replace(/other\.{0,3}/i, '').trim();
                }

                return label.replace(/\bN\s*A\b/g, 'N/A');
              },

              label: context => {
                const chartInstance = context.chart;
                const dataset = chartInstance.data.datasets[context.datasetIndex];
                const activeElements = chartInstance.getActiveElements() ?? [];
                const hasAggregatedHover = activeElements.length > 1;
                const primaryIndex = hasAggregatedHover ? activeElements[0]?.index : null;

                if (hasAggregatedHover && context.dataIndex !== primaryIndex) {
                  return null;
                }

                const resolveValue = (index: number | null | undefined, fallback: number): number => {
                  if (index == null) {
                    return fallback;
                  }
                  const item = this._chartDataList?.[index];
                  if (item && typeof item.additionalValue === 'number') {
                    return item.additionalValue;
                  }
                  const datasetValue = dataset.data[index];
                  if (typeof datasetValue === 'number' && isFinite(datasetValue)) {
                    return datasetValue;
                  }
                  return fallback;
                };

                const defaultValue = context.parsed ?? context.raw ?? 0;
                const aggregatedValue = hasAggregatedHover
                  ? activeElements.reduce((sum, el) => {
                      const val = dataset.data[el.index] as number;
                      return sum + (isFinite(val) ? val : 0);
                    }, 0)
                  : defaultValue;
                const value = hasAggregatedHover
                  ? resolveValue(primaryIndex, aggregatedValue)
                  : resolveValue(context.dataIndex, defaultValue);

                if (!this.useCurrencyPipe) {
                  return `${value}`;
                }

                const pipe = new CustomCurrencyDotsCommasPipe();
                return pipe.transform(value, false);
              },

              labelColor: context => {
                const chart = context.chart;
                const dataset = chart.data.datasets[context.datasetIndex];
                const activeElements = chart.getActiveElements() ?? [];

                const colorIndex = activeElements.length > 1 ? activeElements[0].index : context.dataIndex;
                const colorArray = dataset.backgroundColor as string[];
                const color = colorArray?.[colorIndex] ?? EColorPalette.cGray8;

                return {
                  borderColor: 'transparent',
                  backgroundColor: color,
                  borderWidth: 0,
                };
              },
            },
          },
          legend: { display: false },
        }
      : baseConfig.options?.plugins;

    return new Chart(canvas.nativeElement, {
      ...baseConfig,
      options: {
        ...baseConfig.options,
        animation: this.disableAnimation ? { duration: 0 } : baseConfig.options?.animation,
        transitions: baseConfig.options?.transitions,
        plugins: pluginsBlock,
        onHover: (event, elements) => {
          const target = event?.native?.target as HTMLElement;
          if (!target) return;
          target.style.cursor = elements.length ? 'pointer' : 'default';
          if (this.useCustomTooltipFormatter && elements.length) {
            const index = elements[0].index;
            const item = this._chartDataList[index];
            this.itemHover.emit(item ?? null);
          } else {
            this.itemHover.emit(null);
          }
        },
        onClick: (event, elements) => {
          if (elements.length) {
            const index = elements[0].index;
            const item = this._chartDataList[index];
            if (item) {
              this.itemClick.emit(item);
            }
          }
        },
      },
    });
  }

  private destroyChart() {
    if (this.chart) {
      this.chart.clear();
      this.chart.destroy();
    }
  }
}
